import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import FilterDrawer from "../dashboard/filterDrawer";
import Loader from "../baseComponents/loader";
import FilterListIcon from '@mui/icons-material/FilterAltOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchResultGrid from "../searchResultGrid/searchResultGrid";
import GeneratedDocumentsModal from "./campaignResultsModals/generatedDocumentsModal";
import CallLogsModal from "./campaignResultsModals/callLogsModal";
import CampaignJob from "../../interfaces/campaignJob";
import { DOCUMENT_GENERATION, CALL_CAMPAIGN, KNOW_BOT_CAMPAIGN, CRAFT_AI_BOT_CAMPAIGN } from "../../constants/constants";
import getCampaignJobsColumnDef from "../../colDef/campaignJobsColumnDef";
import Filter from "../../interfaces/filters";
import CampaignService from "../../services/campaignJobService";
import { debounce } from "../../utils/commonUtils";
import { createFilters, getSelectedFiltersCount } from "../../utils/filterUtils";
import Picklist from "../../interfaces/picklist";
import useIsMounted from "../../utils/useMountedRef";

interface CampaignJobsProps {
    campaignType: string;
    setLeftMargin: (val: string) => void;
    setCampaignType: (type: string) => void;
};

const filterKeys = ['campaign_config_name', 'campaign_name', 'campaign_date'];

const CampaignJobs: React.FC<CampaignJobsProps> = ({
    campaignType, setLeftMargin, setCampaignType
}) => {
    const [filters, setFilters] = useState<Filter[]>([]);
    const [filtersCount, setFiltersCount] = useState<number>(0);
    const [documentGenerationJobResultsModalVisible, setDocumentGenerationJobResultsModalVisible] = useState<boolean>(false);
    const [callLogsModalVisible, setCallLogsModalVisible] = useState<boolean>(false);
    const [selectedCampaignJob, setSelectedCampaignJob] = useState<CampaignJob | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [campaignJobs, setCampaignJobs] = useState<CampaignJob[]>([]);
    const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
    const isMountedRef = useIsMounted();

    useEffect(() => {
        setFiltersCount(getSelectedFiltersCount(filters));
    }, [filters]);

    const toggleFilterDrawer = () => {
        setIsFilterOpen(!isFilterOpen);
        setLeftMargin(isFilterOpen ? "16px" : "166px");
    };

    const setDefaultFilters = () => {
        const finalFilters = createFilters(filterKeys, [], [], [], Picklist.createDefault(), [], [], [], []);
        setFilters(finalFilters);
    };
    
    useEffect(() => {
        setDefaultFilters();
    }, []);

    const fetchCampaignJobs = async () => {
        try {
            setIsLoading(true);
            const response = await CampaignService.getCampaignJobs(filters, campaignType);
            if (!isMountedRef.current) return;
            setCampaignJobs(response);
        } catch (_) {} finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    const debouncedFunc = useMemo(() => debounce(fetchCampaignJobs, 500), [filters, campaignType]);

    useEffect(() => {
        if (campaignType) {
            debouncedFunc.cancel();
            debouncedFunc();
            return () => {
                debouncedFunc.cancel();
            };
        }
    }, [filters, campaignType]);

    const openCampaignJobResultsModal = (currentCampaignJob: CampaignJob) => {
        setSelectedCampaignJob(currentCampaignJob);
        if (currentCampaignJob.campaign_type === DOCUMENT_GENERATION) {
            setDocumentGenerationJobResultsModalVisible(true);
        } else if (currentCampaignJob.campaign_type === CALL_CAMPAIGN || currentCampaignJob.campaign_type === KNOW_BOT_CAMPAIGN || currentCampaignJob.campaign_type === CRAFT_AI_BOT_CAMPAIGN) {
            setCallLogsModalVisible(true);
        }
    };

    const closeCampaignJobResultsModal = () => {
        setSelectedCampaignJob(undefined);
        if (selectedCampaignJob?.campaign_type === DOCUMENT_GENERATION) {
            setDocumentGenerationJobResultsModalVisible(false);
        } else if (selectedCampaignJob?.campaign_type === CALL_CAMPAIGN  || selectedCampaignJob?.campaign_type === KNOW_BOT_CAMPAIGN || selectedCampaignJob?.campaign_type === CRAFT_AI_BOT_CAMPAIGN) {
            setCallLogsModalVisible(false);
        }
    };

    const resetFilters = () => {
        setCampaignType('');
        setCampaignJobs([]);
        setDefaultFilters();
    };

    const finalColumnDef = getCampaignJobsColumnDef(openCampaignJobResultsModal);

    return (
        <Box sx={{ ml: 2, mr: 2 }}>
            <FilterDrawer
                filters={filters}
                setFilters={setFilters}
                drawerOpen={isFilterOpen}
                toggleDrawer={toggleFilterDrawer}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button
                    disabled={campaignType === ''}
                    variant={filtersCount === 0 ? "outlined" : "contained"}
                    color="primary"
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={toggleFilterDrawer}
                    aria-label="open drawer"
                >
                    <Typography variant="subtitle2">Filters [{filtersCount}]</Typography>
                </Button>
                <Button
                    style={{ marginLeft: "10px", marginRight: "10px" }}
                    disabled={campaignType === ''}
                    variant="outlined"
                    color="primary"
                    startIcon={<RefreshIcon fontSize="small" />}
                    onClick={resetFilters}
                    aria-label="Refresh"
                >
                    <Typography variant="subtitle2">Reset</Typography>
                </Button>
            </Box>
            {isLoading ? (
                <Loader />
            ) : (
                <SearchResultGrid
                    results={campaignJobs}
                    columns={finalColumnDef}
                    getRowId={(row: CampaignJob) => row.campaign_job_id}
                    onSelectRow={() => {}}
                    rowCount={campaignJobs.length}
                    disableDownloadByPrintOptions
                />
            )}
            {selectedCampaignJob && documentGenerationJobResultsModalVisible && (
                <GeneratedDocumentsModal
                    open={documentGenerationJobResultsModalVisible}
                    onClose={closeCampaignJobResultsModal}
                    currentCampaignJob={selectedCampaignJob}
                />
            )}
            {selectedCampaignJob && callLogsModalVisible && (
                <CallLogsModal
                    open={callLogsModalVisible}
                    onClose={closeCampaignJobResultsModal}
                    currentCampaignJob={selectedCampaignJob}
                />
            )}
        </Box>
    );
};

export default CampaignJobs;